import React, { useCallback, useMemo, useState } from "react";
import { useUserProfile } from "../../hooks/useUserProfile";
import "./OnboardingTutorial.scss";
import Step1FindBars from "./Step1FindBars";
import Step2CheckIn from "./Step2CheckIn";
import Step3PlayAndClimb from "./Step3PlayAndClimb";
import Step4SetHandle from "./Step4SetHandle";

interface OnboardingTutorialProps {
  userUuid?: string;
  onFinish: () => void;
  onSkip: () => void;
}

const OnboardingTutorial: React.FC<OnboardingTutorialProps> = ({
  userUuid,
  onFinish,
  onSkip,
}) => {
  const { profile } = useUserProfile(userUuid);
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;

  const handleNext = useCallback(() => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      onFinish();
    }
  }, [currentStep, totalSteps, onFinish]);

  const handlePrevious = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const handleSkip = useCallback(() => {
    if (profile?.username) {
      onSkip();
    } else {
      setCurrentStep(4);
    }
  }, [profile, onSkip]);

  const renderStepContent = useCallback(() => {
    switch (currentStep) {
      case 1:
        return <Step1FindBars />;
      case 2:
        return <Step2CheckIn />;
      case 3:
        return <Step3PlayAndClimb />;
      case 4:
        return (
          <Step4SetHandle
            onNext={handleNext}
            handle={profile?.username || ""}
          />
        );
      default:
        return null;
    }
  }, [currentStep, handleNext, profile]);

  const dots = useMemo(() => {
    return (
      <div className="step-dots">
        {[...Array(totalSteps)].map((_, index) => (
          <span
            key={index}
            className={`dot ${currentStep === index + 1 ? "active" : ""}`}
          ></span>
        ))}
      </div>
    );
  }, [currentStep, totalSteps]);

  return (
    <div className="onboarding-tutorial-overlay">
      <div className="onboarding-tutorial-container">
        {currentStep === totalSteps ? null : (
          <button className="skip-button" onClick={handleSkip}>
            Skip
          </button>
        )}
        <div className="step-content">{renderStepContent()}</div>
        {dots}
        <div className="navigation-buttons">
          <button
            className="btn btn-outline-primary"
            onClick={handlePrevious}
            disabled={currentStep === 1}
          >
            Previous
          </button>
          {currentStep === totalSteps ? null : (
            <button className="btn btn-primary" onClick={handleNext}>
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingTutorial;
